import { PencilIcon } from '@heroicons/react/24/solid';
import { Edge, Node } from '@xyflow/react';
import { useMemo, useState, useEffect } from 'react';
import Button from 'src/components/button/button';
import FormSwitch from 'src/components/form-switch';
import { useImportConfiguration } from 'src/context/import-configuration';
import { allIdsReviewed } from 'src/context/import-configuration/import-review-helpers';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { getEdgeName } from 'src/utils/node';

import { DialogCustomEdgeEdit } from '../../edges/dialog-edit/index';
import { IEdgeExtraData } from '../../edges/types';

export default function ImportStepConnectionsReview() {
  const { dialogState, updateProjectJsonData, importReview, setEdgeReviewed } =
    useImportConfiguration();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<IEdgeExtraData>({});

  const project = dialogState?.projectJsonData;
  const edges = project?.edges;
  const nodes = project?.nodes;

  const ids = useMemo(() => {
    if (!edges) return [];
    return edges.map((e) => e.id);
  }, [edges]);

  useEffect(() => {
    if (!editingId || !edges) return;
    const edge = edges.find((e) => e.id === editingId);
    setDraft((edge?.data || {}) as IEdgeExtraData);
  }, [editingId, edges]);

  if (!project || !edges || !nodes) return null;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          Edit connections with the same dialog as on the canvas (style, VLAN,
          link, cable…). Confirm each row when done.
        </p>
        <Button
          type="button"
          color={
            allIdsReviewed(ids, importReview.edgeReviewed) ? 'green' : 'blue'
          }
          disabled={allIdsReviewed(ids, importReview.edgeReviewed)}
          onClick={() => {
            setEdgeReviewed(ids, true);
          }}
        >
          {allIdsReviewed(ids, importReview.edgeReviewed)
            ? 'Confirmed ✓'
            : 'Confirm All'}
        </Button>
      </div>

      <div className="space-y-2 overflow-y-auto pr-1">
        {edges.map((edge) => {
          const confirmed = !!importReview.edgeReviewed[edge.id];
          const label = getEdgeName(edge.id, nodes as Node<NodeModel>[]);

          return (
            <div
              key={edge.id}
              className="rounded border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-1 flex-row items-center justify-start gap-4">
                  <FormSwitch
                    id={`confirmed-${edge.id}`}
                    name={`confirmed-${edge.id}`}
                    wrapperClassName="w-auto"
                    checked={confirmed}
                    onChange={(value) => setEdgeReviewed([edge.id], value)}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800">{label}</p>
                    <p className="font-mono text-xs text-gray-500">{edge.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    color="blue"
                    onClick={() => setEditingId(edge.id)}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {edges.length === 0 && (
        <p className="text-sm text-gray-500">No connections.</p>
      )}

      {editingId && (
        <DialogCustomEdgeEdit
          ids={[editingId]}
          data={draft}
          onChangeEdgeData={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          onCancel={() => setEditingId(null)}
          onSave={(_newData, closeDialog = true) => {
            updateProjectJsonData((prev) => ({
              ...prev,
              edges: (prev.edges as Edge[]).map((e) =>
                e.id === editingId
                  ? { ...e, data: { ...e.data, ...draft } }
                  : e,
              ),
              lastChanged: new Date().toISOString(),
            }));
            setEdgeReviewed([editingId], true);
            if (closeDialog) {
              setEditingId(null);
            }
          }}
        />
      )}
    </div>
  );
}
